import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Activity,
  Network,
  Eye,
  Tag,
  ServerCog,
  Gauge,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import SEO from "../components/site/SEO";
import SectionHeader from "../components/site/SectionHeader";

const SERVICES = [
  {
    id: "itmon",
    icon: Gauge,
    name: "IT Infrastructure Monitoring",
    title: "IT Infrastructure Monitoring",
    desc: "Monitor the IT infrastructure that matters most to your business with flexible, lightweight and highly customisable monitoring.",
    bullets: [
      "Choose what to monitor: servers, databases, cloud, applications",
      "Configure intervals, thresholds and check conditions",
      "Assign Critical / High / Medium / Low priorities",
      "Lightweight architecture with configurable intervals",
      "Alert routing, escalation and ticket integration",
    ],
    href: "/it-infrastructure-monitoring",
    tag: "NEW",
  },
  {
    id: "infra",
    icon: ServerCog,
    name: "Infrastructure Monitoring & Support",
    title: "Infrastructure Monitoring & Support",
    desc: "24/7 monitoring and proactive support across servers, cloud workloads, databases, and network infrastructure — keeping your digital backbone healthy and incident-ready.",
    bullets: [
      "Server, database, and application health monitoring",
      "Cloud workload monitoring across AWS, Azure & GCP",
      "Proactive patching, hardening & update management",
      "L1/L2 support with documented runbooks",
      "SLA-backed uptime reporting & monthly health reviews",
    ],
  },
  {
    id: "vapt",
    icon: ShieldCheck,
    name: "VAPT",
    title: "Vulnerability Assessment & Penetration Testing",
    desc: "Adversary-grade testing with audit-ready reports. We map every attack path before threat actors do.",
    bullets: [
      "External & internal network pen testing",
      "Web, API, and mobile application testing",
      "Cloud config & IAM review (AWS, Azure, GCP)",
      "Re-test included in every engagement",
    ],
  },
  {
    id: "threat",
    icon: Activity,
    name: "Threat Protection",
    title: "Real-time Threat Detection & Response",
    desc: "Continuous detection, triage, and containment by L2 SOC analysts with playbook-driven response.",
    bullets: [
      "MTTD under 60 seconds across covered estate",
      "Tier-2 analyst eyes on every High/Critical alert",
      "Playbook-driven containment & rollback",
      "Integrated with your SIEM, EDR, and ticketing",
    ],
  },
  {
    id: "network",
    icon: Network,
    name: "Network Monitoring",
    title: "Network Device Monitoring",
    desc: "Deep telemetry across firewalls, switches, and endpoints with anomaly correlation and instant alerts.",
    bullets: [
      "Firewall, router, switch, and endpoint coverage",
      "Anomaly correlation across layers",
      "Instant Slack / Teams / PagerDuty alerts",
      "Capacity & uptime trend reports",
    ],
  },
  {
    id: "e2e",
    icon: Eye,
    name: "E2E Security Monitoring",
    title: "End-to-End Security Monitoring Services",
    desc: "Single pane-of-glass coverage from cloud to endpoint, fully managed by Quasect's L2 SOC.",
    bullets: [
      "Cloud, network, endpoint, and identity coverage",
      "Single pane-of-glass dashboard",
      "Compliance evidence captured continuously",
      "Quarterly business reviews with the SOC lead",
    ],
  },
  {
    id: "whitelabel",
    icon: Tag,
    name: "White Label Solution",
    title: "White Label Solution",
    desc: "We provide White Label solutions for clients, enabling businesses to offer advanced cybersecurity monitoring, threat detection, incident response, and managed security services under their own brand.",
    bullets: [
      "Fully rebrandable dashboards & client portals",
      "Co-branded VAPT, incident & compliance reports",
      "Tier-1 & Tier-2 analyst capacity on demand",
      "Integration with your existing CRM and ticketing",
      "Revenue-share & flat-fee partnership models",
    ],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-[#050507] text-white">
      <SEO
        title="Quasect Services — VAPT, Threat Protection, Network & E2E Monitoring"
        description="Quasect's SOC Level 2 service catalog: VAPT, real-time threat protection, network device monitoring, and end-to-end security monitoring."
        path="/services"
      />

      <section className="relative overflow-hidden noise border-b border-white/10">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3">
            <span className="w-6 h-px bg-[#39FF14]" /> SERVICES / SOC L2
          </div>
          <h1 className="font-display font-black mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] tracking-tighter leading-[1.02] max-w-5xl break-words">
            Seven pillars of <span className="text-[#39FF14]">enterprise defense.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#8B93A5] max-w-3xl leading-relaxed">
            Each service is delivered as a managed engagement by Quasect's L2 SOC. Combine them
            into a full E2E program or run them independently.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 space-y-6">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.05 }}
              data-testid={`service-detail-${s.id}`}
              className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 p-8 md:p-12 transition-all relative"
            >
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 border border-white/15 flex items-center justify-center group-hover:border-[#39FF14] group-hover:bg-[#39FF14]/10 transition-all">
                      <s.icon className="w-6 h-6 text-[#00F0FF] group-hover:text-[#39FF14] transition-colors" />
                    </div>
                    <span className="font-mono text-[11px] tracking-widest uppercase text-[#8B93A5]">
                      0{i + 1} / 07 · {s.name}
                    </span>
                    {s.tag && (
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase border border-[#39FF14]/40 text-[#39FF14] px-2 py-0.5 bg-[#39FF14]/5">
                        {s.tag}
                      </span>
                    )}
                  </div>
                  <h2 className="font-display font-bold text-3xl md:text-4xl mt-6 leading-tight tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-[#8B93A5] leading-relaxed">{s.desc}</p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    {s.href && (
                      <Link
                        to={s.href}
                        data-testid={`service-explore-${s.id}`}
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-wider bg-[#39FF14] text-black font-bold hover:bg-[#32E612] px-5 py-2.5 transition-colors"
                      >
                        Explore {s.name} <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.id}`}
                      className="inline-flex items-center gap-2 text-sm uppercase tracking-wider border border-white/20 hover:border-white px-4 py-2.5 transition-colors"
                    >
                      Scope this service <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-4">
                    What's included
                  </div>
                  <ul className="space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-white/85">
                        <CheckCircle2 className="w-5 h-5 text-[#39FF14] shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="How we engage"
            title="Three steps from intake to coverage."
          />
          <div className="mt-12 grid md:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                n: "01",
                t: "Discovery & Scoping",
                d: "30-min call with a SOC lead. We map your environment, current tooling, and threat profile.",
              },
              {
                n: "02",
                t: "Onboarding & Integration",
                d: "We integrate with your SIEM, EDR, identity, and ticketing. Detections tuned in 14 days or less.",
              },
              {
                n: "03",
                t: "24/7 Coverage",
                d: "L2 analysts on rotation. Weekly reports, monthly retros, quarterly business reviews.",
              },
            ].map((p) => (
              <div
                key={p.n}
                className="bg-[#0A0A0D] border border-white/10 hover:border-[#39FF14]/40 p-7 md:p-8 transition-colors"
                data-testid={`process-step-${p.n}`}
              >
                <div className="font-display font-black text-3xl text-[#39FF14]/40">{p.n}</div>
                <h3 className="font-display font-bold text-xl mt-4">{p.t}</h3>
                <p className="text-[#8B93A5] mt-3 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
